import type { ManagedStreamingDetails } from '@/types/managedStreaming';
import { adminManagedStreamingData } from '@/mocks/adminManagedStreaming';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface RadioSettingsData {
  general: GeneralSettings;
  branding: BrandingSettings;
  frecuencia: FrecuenciaSettings;
  dominio: DominioSettings;
  qr: QRSettings;
  redes: RedesSettings;
  contacto: ContactoSettings;
  apps: AppsSettings;
  notificaciones: NotificacionesSettings;
  managedStreaming: ManagedStreamingDetails;
}

export interface GeneralSettings {
  nombreRadio: string;
  eslogan: string;
  descripcion: string;
  ciudad: string;
  pais: string;
  categoria: string;
  idioma: string;
  zonaHoraria: string;
  email: string;
}

export interface BrandingSettings {
  logo: string;
  cover: string;
  colorPrimary: string;
  colorSecondary: string;
  colorAccent: string;
  tipografia: string;
}

export interface FrecuenciaSettings {
  streamUrl: string;
  streamPort: number;
  streamPassword: string;
  formato: string;
  bitrate: string;
  estado: 'online' | 'offline';
  autoReconexion: boolean;
  reintentos: number;
  icecastUrl: string;
  frecuenciaNum: string;
  frecuenciaWeb: string;
}

export interface DominioSettings {
  dominioActual: string;
  estado: 'no_configurado' | 'pendiente_dns' | 'verificado' | 'activo' | 'error';
  fechaVerificacion: string;
  dnsCname: string;
  dnsTxt: string;
}

export interface QRSettings {
  urlPermanente: string;
  redirectCode: string;
  destinoTipo: 'custom_domain' | 'rfe_page';
  destinoUrl: string;
  destinoLabel: string;
}

export interface SocialNetwork {
  id: string;
  platform: string;
  url: string;
  icon: string;
  activo: boolean;
}

export interface RedesSettings {
  redes: SocialNetwork[];
}

export interface ContactoSettings {
  email: string;
  telefono: string;
  whatsapp: string;
  direccion: string;
  ciudadContacto: string;
  paisContacto: string;
  mostrarEmail: boolean;
  mostrarTelefono: boolean;
  mostrarWhatsapp: boolean;
}

export interface AppsSettings {
  iosAppUrl: string;
  androidAppUrl: string;
  iosVersion: string;
  androidVersion: string;
  colorTint: string;
}

export interface NotificacionesSettings {
  emailNuevoOyente: boolean;
  emailMensajeCabina: boolean;
  emailNuevoSeguidor: boolean;
  emailReporteSemanal: boolean;
  pushNuevoMensaje: boolean;
  pushAlertaStream: boolean;
  pushRecordatorio: boolean;
}

export interface StatusOverview {
  frecuencia: { estado: 'online' | 'offline'; label: string };
  dominio: { estado: string; label: string; colorClass: string };
  plan: { nombre: string; estado: string; label: string; colorClass: string };
  licencia: { estado: string; label: string; vencimiento: string; colorClass: string };
}

export const defaultRadioSettings: RadioSettingsData = {
  general: {
    nombreRadio: 'Radio Nueva Actitud',
    eslogan: 'La radio que te mueve',
    descripcion: 'La mejor música latina y los éxitos del momento. Transmitiendo desde Santa Rosa, Guatemala para todo el mundo.',
    ciudad: 'Santa Rosa',
    pais: 'Guatemala',
    categoria: 'Música Latina',
    idioma: 'Español',
    zonaHoraria: 'America/Guatemala (GMT-6)',
    email: 'contacto@nuevaactitud.com',
  },
  branding: {
    logo: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20logo%20design%2C%20minimalist%20circular%20emblem%2C%20warm%20coral%20and%20cream%20colors%2C%20sound%20wave%20and%20broadcast%20tower%20motif%2C%20clean%20professional%20brand%20identity%2C%20elegant%20typography&width=200&height=200&seq=admin-settings-logo&orientation=squarish',
    cover: 'https://readdy.ai/api/search-image?query=Radio%20station%20cover%20art%20background%2C%20warm%20ambient%20lighting%20with%20abstract%20sound%20waves%2C%20coral%20and%20amber%20gradient%20tones%2C%20modern%20minimalist%20aesthetic%2C%20audio%20broadcasting%20theme%2C%20soft%20elegant%20atmosphere&width=1600&height=400&seq=admin-settings-cover&orientation=landscape',
    colorPrimary: '#f97316',
    colorSecondary: '#fbbf24',
    colorAccent: '#ec4899',
    tipografia: 'Inter',
  },
  frecuencia: {
    streamUrl: 'https://stream.nuevaactitud.com/live',
    streamPort: 8000,
    streamPassword: '••••••••••••',
    formato: 'MP3',
    bitrate: '192 kbps',
    estado: 'online',
    autoReconexion: true,
    reintentos: 5,
    icecastUrl: 'https://icecast.nuevaactitud.com/admin',
    frecuenciaNum: '94.70 RFW',
    frecuenciaWeb: 'nueva-actitud',
  },
  dominio: {
    dominioActual: 'nuevaactitud.com',
    estado: 'activo',
    fechaVerificacion: '12/03/2026',
    dnsCname: 'radiofrecuenciaweb.com',
    dnsTxt: 'rfw-verify=xk7md92ls0anqp3',
  },
  qr: {
    urlPermanente: 'radiofrecuenciaweb.com/r/nueva-actitud',
    redirectCode: 'nueva-actitud',
    destinoTipo: 'rfe_page',
    destinoUrl: '/r/nueva-actitud',
    destinoLabel: 'Perfil en RadioFrecuenciaWeb',
  },
  redes: {
    redes: [
      { id: 'facebook', platform: 'Facebook', url: 'https://facebook.com/nuevaactitud', icon: 'ri-facebook-fill', activo: true },
      { id: 'instagram', platform: 'Instagram', url: 'https://instagram.com/nuevaactitud', icon: 'ri-instagram-fill', activo: true },
      { id: 'twitter', platform: 'X (Twitter)', url: 'https://x.com/nuevaactitud', icon: 'ri-twitter-x-fill', activo: true },
      { id: 'youtube', platform: 'YouTube', url: 'https://youtube.com/@nuevaactitud', icon: 'ri-youtube-fill', activo: true },
      { id: 'tiktok', platform: 'TikTok', url: 'https://tiktok.com/@nuevaactitud', icon: 'ri-tiktok-fill', activo: false },
      { id: 'whatsapp', platform: 'WhatsApp', url: 'https://wa.me/50255551234', icon: 'ri-whatsapp-fill', activo: true },
      { id: 'telegram', platform: 'Telegram', url: '', icon: 'ri-telegram-fill', activo: false },
      { id: 'spotify', platform: 'Spotify', url: 'https://open.spotify.com/show/nuevaactitud', icon: 'ri-spotify-fill', activo: false },
      { id: 'discord', platform: 'Discord', url: 'https://discord.gg/nuevaactitud', icon: 'ri-discord-fill', activo: false },
      { id: 'twitch', platform: 'Twitch', url: '', icon: 'ri-twitch-fill', activo: false },
    ],
  },
  contacto: {
    email: 'contacto@nuevaactitud.com',
    telefono: '+502 5555 1234',
    whatsapp: '+502 5555 1234',
    direccion: 'Av. Central 456, Zona 1',
    ciudadContacto: 'Santa Rosa',
    paisContacto: 'Guatemala',
    mostrarEmail: true,
    mostrarTelefono: true,
    mostrarWhatsapp: true,
  },
  apps: {
    iosAppUrl: 'https://apps.apple.com/app/radio-nueva-actitud/id123456789',
    androidAppUrl: 'https://play.google.com/store/apps/details?id=com.nuevaactitud',
    iosVersion: '2.4.1',
    androidVersion: '2.4.1',
    colorTint: '#f97316',
  },
  notificaciones: {
    emailNuevoOyente: true,
    emailMensajeCabina: true,
    emailNuevoSeguidor: false,
    emailReporteSemanal: true,
    pushNuevoMensaje: true,
    pushAlertaStream: true,
    pushRecordatorio: false,
  },
  managedStreaming: adminManagedStreamingData,
};

export const categoriasRadio = [
  'Música Latina', 'Pop', 'Rock', 'Electrónica', 'Jazz & Blues',
  'Clásica', 'Noticias', 'Deportes', 'Talk Show', 'Religiosa',
  'Comunitaria', 'Cultural',
];

export const paisesLatam = [
  'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia',
  'Costa Rica', 'Cuba', 'Ecuador', 'El Salvador', 'Guatemala',
  'Honduras', 'México', 'Nicaragua', 'Panamá', 'Paraguay',
  'Perú', 'República Dominicana', 'Uruguay', 'Venezuela',
  'España', 'Estados Unidos',
];

export const formatosStream = ['MP3', 'AAC', 'AAC+', 'OGG', 'FLAC', 'WAV'];
export const bitrates = ['64 kbps', '96 kbps', '128 kbps', '192 kbps', '256 kbps', '320 kbps'];
export const tipografias = ['Inter', 'Poppins', 'Montserrat', 'Space Grotesk', 'DM Sans', 'Outfit', 'Sora', 'Manrope'];

export const sectionNavItems = [
  { id: 'general', label: 'General', icon: 'ri-settings-3-line', description: 'Información básica de la radio' },
  { id: 'branding', label: 'Branding', icon: 'ri-palette-line', description: 'Logo, colores y apariencia' },
  { id: 'frecuencia', label: 'Frecuencia RFW', icon: 'ri-broadcast-line', description: 'Streaming y emisión' },
  { id: 'dominio', label: 'Dominio', icon: 'ri-global-line', description: 'Dominio personalizado' },
  { id: 'qr', label: 'QR', icon: 'ri-qr-code-line', description: 'Código QR permanente' },
  { id: 'redes', label: 'Redes', icon: 'ri-share-line', description: 'Redes sociales y enlaces' },
  { id: 'contacto', label: 'Contacto', icon: 'ri-mail-line', description: 'Email, teléfono y dirección' },
  { id: 'apps', label: 'Apps', icon: 'ri-smartphone-line', description: 'App iOS y Android' },
  { id: 'notificaciones', label: 'Notificaciones', icon: 'ri-notification-3-line', description: 'Alertas y avisos' },
] as const;

export const statusOverviewData: StatusOverview = {
  frecuencia: { estado: 'online', label: 'Transmitiendo 94.70 RFW — 192 kbps MP3' },
  dominio: { estado: 'activo', label: 'nuevaactitud.com — Activo', colorClass: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  plan: { nombre: 'Profesional', estado: 'activo', label: 'Plan Profesional — USD 29.99/mes', colorClass: 'bg-primary-50 border-primary-200 text-primary-700' },
  licencia: { estado: 'vigente', label: 'Vigente', vencimiento: 'Renovación: 15/07/2026', colorClass: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
};